import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { Card, CardContent } from '../../../components/ui/Card.js';
import { Button } from '../../../components/ui/Button.js';
import { Input, FormField } from '../../../components/ui/FormField.js';
import { FormSelect } from '../../../components/ui/FormSelect.js';
import { StatusBadge } from '../../../components/ui/StatusBadge.js';
import { Money } from '../../../components/ui/Money';
import { useToast } from '../../../components/ui/Toast.js';
import { PageHeader } from '../../../components/layout/PageHeader.js';
import { ArrowLeftRight, Check, X, AlertTriangle } from 'lucide-react';
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '../../../lib/locale';
import { useDebounce, usePageTitle } from '../../../lib/hooks.js';
import Decimal from 'decimal.js';
import { useNameTransfers, useCreateSubscriberTransfer, useSchemes, useSchemeSubscribers, type SubscriberListItem } from '../../../features/schemes/network/index.js';
import { useCustomers } from '../../../features/customers/hooks.js';
import type { CustomerListItem } from '../../../features/customers/types.js';
import { approvalsHooks } from '../../../features/approvals/network/endpoints.js';

export const Route = createFileRoute('/_auth/schemes/transfers')({
  validateSearch: z.object({
    schemeId: z.string().optional(),
    subscriberId: z.string().optional(),
  }),
  component: NameTransfersPage,
});

interface NameTransferRequest {
  id: string;
  schemeName: string;
  subscriberTicket: string;
  fromCustomerName: string;
  toCustomerName: string;
  paiseTransferValue: number;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  createdAt: string;
  remarks: string;
}

function NameTransfersPage() {
  usePageTitle('Scheme Transfers');
  const { toast } = useToast();
  const search = Route.useSearch();
  const { data: response, isLoading: transfersLoading } = useNameTransfers();
  const requests = (response?.data ?? []) as NameTransferRequest[];
  const [activeTab, setActiveTab] = React.useState<'create' | 'queue'>('create');

  const { data: schemesResponse } = useSchemes();
  const schemes = schemesResponse?.data;
  const [schemeId, setSchemeId] = React.useState(search.schemeId ?? '');
  const { data: subscribersResponse, isLoading: subscribersLoading } = useSchemeSubscribers(schemeId, { page: 1, limit: 100 });
  const subscribers = subscribersResponse?.data ?? [];
  const [subscriberId, setSubscriberId] = React.useState(search.subscriberId ?? '');
  const selectedSubscriber = subscribers.find((s: SubscriberListItem) => s.id === subscriberId) ?? null;

  const [customerSearch, setCustomerSearch] = React.useState('');
  const debouncedCustomerSearch = useDebounce(customerSearch, 300);
  const [selectedCustomer, setSelectedCustomer] = React.useState<CustomerListItem | null>(null);
  const { data: customerLookupResponse, isLoading: searchingCustomers } = useCustomers({ page: 1, limit: 10, query: debouncedCustomerSearch || undefined });

  const [valuePaise, setValuePaise] = React.useState('');
  const [remarks, setRemarks] = React.useState('');

  const createMutation = useCreateSubscriberTransfer();
  const approveMutation = approvalsHooks.useApprove();
  const rejectMutation = approvalsHooks.useReject();

  const [rejectingId, setRejectingId] = React.useState<string | null>(null);
  const [rejectReason, setRejectReason] = React.useState('');
  const [rejectError, setRejectError] = React.useState<string | undefined>();

  const resetForm = () => { setSchemeId(''); setSubscriberId(''); setCustomerSearch(''); setSelectedCustomer(null); setValuePaise(''); setRemarks(''); };

  const handleCreateTransfer = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSubscriber) { toast({ type: 'error', title: 'Error', description: 'Select the chit scheme and the ticket being transferred.' }); return; }
    if (!selectedCustomer) { toast({ type: 'error', title: 'Error', description: 'Search and select the new owner (target customer).' }); return; }
    if (selectedCustomer.id === selectedSubscriber.customerId) { toast({ type: 'error', title: 'Invalid Transfer', description: 'The new owner must be different from the current subscriber.' }); return; }
    const rupees = new Decimal(valuePaise || 0).toNumber();
    if (isNaN(rupees) || rupees <= 0) { toast({ type: 'error', title: 'Invalid Value', description: 'Please enter a valid transfer valuation.' }); return; }
    createMutation.mutate(
      { subscriberId: selectedSubscriber.id, newCustomerId: selectedCustomer.id, valuePaise: new Decimal(rupees).mul(100).toNumber(), remarks: remarks || undefined },
      {
        onSuccess: () => {
          toast({ type: 'success', title: 'Transfer Submitted', description: 'The mid-cycle name transfer has been locked and sent to the Approvals queue.' });
          resetForm(); setActiveTab('queue');
        },
        onError: (err: unknown) => { toast({ type: 'error', title: 'Failed to Submit Transfer', description: (err as Error)?.message || 'Could not submit the name transfer request. Please try again.' }); },
      }
    );
  };

  const handleApprove = (id: string) => {
    approveMutation.mutate({ type: 'SUBSCRIBER_TRANSFER', id }, {
      onSuccess: () => { toast({ type: 'success', title: 'Swapped & Reassigned', description: 'Subscription re-allocated to the new owner.' }); },
      onError: (err: unknown) => { toast({ type: 'error', title: 'Approval Failed', description: (err as Error)?.message || 'Could not approve the transfer request.' }); },
    });
  };

  const openRejectModal = (id: string) => { setRejectingId(id); setRejectReason(''); setRejectError(undefined); };

  const handleReject = () => {
    if (!rejectingId) return;
    if (rejectReason.trim().length < 20) { setRejectError('Rejection reason must be at least 20 characters.'); return; }
    rejectMutation.mutate({ type: 'SUBSCRIBER_TRANSFER', id: rejectingId, reason: rejectReason.trim() }, {
      onSuccess: () => { toast({ type: 'success', title: 'Transfer Rejected', description: 'Name transfer cancelled. Original subscriber retains ownership.' }); setRejectingId(null); setRejectReason(''); },
      onError: (err: unknown) => { setRejectError((err as Error)?.message || 'Could not reject the transfer request.'); },
    });
  };

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title="Subscriber Name Transfer"
        subtitle="Swap chit subscription ownership mid-cycle. Reassigns ledger books, past credit assets, and future dues atomically."
        backTo="/schemes"
      />
      <div className="flex gap-4 border-b border-border mb-6">
        <Button variant="ghost" onClick={() => setActiveTab('create')}
          className={`pb-2 px-1 text-sm font-semibold transition-colors border-b-2 ${activeTab === 'create' ? 'border-accent-500 text-text-primary' : 'border-transparent text-text-muted hover:text-text-primary'}`}
        >
          Initiate Owner Swap
        </Button>
        <Button variant="ghost" onClick={() => setActiveTab('queue')}
          className={`pb-2 px-1 text-sm font-semibold transition-colors border-b-2 ${activeTab === 'queue' ? 'border-accent-500 text-text-primary' : 'border-transparent text-text-muted hover:text-text-primary'}`}
        >
          Pending Swaps ({requests.filter(r => r.status === 'PENDING').length})
        </Button>
      </div>

      {activeTab === 'create' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          <div className="lg:col-span-2">
            <Card className="shadow-sm">
              <CardContent className="p-6">
                <form onSubmit={handleCreateTransfer} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <FormSelect label="Target Chit Scheme" required value={schemeId} onChange={e => { setSchemeId(e.target.value); setSubscriberId(''); }}
                      options={[{ value: '', label: 'Select a scheme...' }, ...(schemes ?? []).map(s => ({ value: s.id, label: `${s.name} (${s.schemeNumber})` }))]}
                    />
                    <FormSelect label="Swapping Ticket / Subscriber" required value={subscriberId} onChange={e => setSubscriberId(e.target.value)} disabled={!schemeId || subscribersLoading}
                      options={[{ value: '', label: subscribersLoading ? 'Loading subscribers...' : 'Select a subscriber...' },
                        ...subscribers.map((s: SubscriberListItem) => ({ value: s.id, label: `TKT-${(s.ticketNumbers || []).join(',')} — ${s.customerName} (${s.status})` }))]}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField label="Current Owner (From Customer)">
                      <Input value={selectedSubscriber ? `${selectedSubscriber.customerName} (${selectedSubscriber.customerMobile})` : ''} readOnly disabled />
                    </FormField>
                    <FormField label="New Owner (To Customer)" required>
                      {!selectedCustomer ? (
                        <div className="space-y-1">
                          <Input placeholder="Search by name, mobile, or ID..." value={customerSearch} onChange={e => setCustomerSearch(e.target.value)} />
                          {searchingCustomers && <div className="text-xs text-text-muted">Searching...</div>}
                          {customerSearch && (
                            <div className="max-h-40 overflow-y-auto divide-y divide-border border border-border rounded-[6px] bg-surface-1">
                              {customerLookupResponse?.data?.map((customer: CustomerListItem) => (
                                <Button key={customer.id} type="button" onClick={() => setSelectedCustomer(customer)}
                                  className="w-full text-left px-3 py-2 text-sm text-text-primary hover:bg-surface-2 transition-colors flex justify-between"
                                >
                                  <span className="font-medium">{customer.name}</span>
                                  <span className="text-text-secondary text-xs">{customer.mobile}</span>
                                </Button>
                              ))}
                              {!searchingCustomers && customerLookupResponse?.data?.length === 0 && (
                                <div className="p-3 text-sm text-text-muted text-center">No customers found.</div>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="bg-surface-2 p-2 rounded-[6px] border border-border flex justify-between items-center">
                          <div><p className="text-sm font-semibold text-text-primary">{selectedCustomer.name}</p><p className="text-xs text-text-secondary">Mobile: {selectedCustomer.mobile}</p></div>
                          <Button type="button" variant="secondary" size="sm" onClick={() => setSelectedCustomer(null)}>Change</Button>
                        </div>
                      )}
                    </FormField>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField label="Current Swapping Valuation (Paid in Rupees)" required>
                      <Input type="number" value={valuePaise} onChange={e => setValuePaise(e.target.value)} />
                    </FormField>
                    <FormField label="Transfer Reason & Board Memo">
                      <Input placeholder="e.g. Relocation, Swapping due to emergency..." value={remarks} onChange={e => setRemarks(e.target.value)} />
                    </FormField>
                  </div>
                  <Button type="submit" variant="primary" className="w-full" disabled={createMutation.isPending}>
                    <ArrowLeftRight className="w-4 h-4 mr-2" />
                    {createMutation.isPending ? 'Submitting...' : 'Lock Swap and Submit For Board Verification'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-warning-bg border border-warning-500 rounded-xl p-5 text-warning-text space-y-3">
              <div className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-warning-text shrink-0" /><h4 className="font-bold text-sm">Critical Swap Ledger Warning</h4></div>
              <p className="text-xs leading-relaxed">SWAPPING chit tickets shifts <span className="font-bold">ALL</span> past dividend assets, total ledger subscription savings, and outstanding default liabilities from the previous owner to the target client.</p>
              <div className="text-xs space-y-1 bg-surface-1/60 p-3 rounded-lg border border-warning-bg font-medium">
                <p>• Atomic swapping occurs ONLY on approval.</p><p>• Balances and liability books swap in full.</p><p>• Both accounts must have clean KYC profiles.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'queue' && (
        <div className="bg-surface-1 border border-border rounded-xl overflow-hidden shadow-sm max-w-5xl">
          <table className="w-full border-collapse text-left text-sm">
            <thead className="bg-surface-2 text-text-secondary font-semibold uppercase tracking-wider text-xs border-b border-border">
              <tr>
                <th className="p-4">Chit & Ticket</th>
                <th className="p-4">Original Owner</th>
                <th className="p-4">Proposed Target Owner</th>
                <th className="p-4">Asset Value Swapped</th>
                <th className="p-4">Date Swapped</th>
                <th className="p-4">Swap Status</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border text-text-primary">
              {requests.map(r => (
                <tr key={r.id} className="hover:bg-surface-2 transition-colors">
                  <td className="p-4">
                    <div className="flex flex-col"><span className="font-semibold text-text-primary">{r.schemeName}</span><span className="font-mono text-xs text-text-muted mt-0.5">{r.subscriberTicket}</span></div>
                  </td>
                  <td className="p-4 font-medium text-danger-600">{r.fromCustomerName}</td>
                  <td className="p-4 font-medium text-success-text">{r.toCustomerName}</td>
                  <td className="p-4"><Money paise={r.paiseTransferValue} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} /></td>
                  <td className="p-4 font-mono text-xs">{r.createdAt}</td>
                  <td className="p-4"><StatusBadge status={r.status === 'APPROVED' ? 'success' : r.status === 'REJECTED' ? 'danger' : 'warning'} label={r.status} /></td>
                  <td className="p-4 text-right">
                    {r.status === 'PENDING' ? (
                      <div className="flex gap-2 justify-end">
                        <Button variant="secondary" size="sm" onClick={() => handleApprove(r.id)} disabled={approveMutation.isPending}
                          aria-label="Approve transfer" className="bg-success-bg hover:bg-success-bg/80 text-success-text border-success-bg"><Check className="w-3.5 h-3.5" /></Button>
                        <Button variant="secondary" size="sm" onClick={() => openRejectModal(r.id)} disabled={rejectMutation.isPending}
                          aria-label="Reject transfer" className="bg-danger-bg hover:bg-danger-bg/80 text-danger-text border-danger-bg"><X className="w-3.5 h-3.5" /></Button>
                      </div>
                    ) : <span className="text-xs text-text-muted font-mono italic">Verified Swap</span>}
                  </td>
                </tr>
              ))}
              {!transfersLoading && requests.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center text-text-muted">No owner swaps records found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {rejectingId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-modal p-4">
          <div className="bg-surface-1 p-6 rounded-[14px] shadow-lg max-w-md w-full border border-border space-y-3">
            <h3 className="text-lg font-semibold text-text-primary">Reject Name Transfer</h3>
            <FormField label="Rejection Reason" required>
              <textarea value={rejectReason} onChange={e => { setRejectReason(e.target.value); setRejectError(undefined); }}
                placeholder="Provide a detailed reason for rejection..." rows={3}
                className={`w-full px-3 py-2 text-sm bg-surface-1 border rounded focus:outline-none focus:ring-1 text-text-primary font-medium ${rejectError ? 'border-danger-500 focus:ring-danger-500' : 'border-border focus:ring-danger-500'}`}
              />
            </FormField>
            <div className="flex justify-between items-center text-xs">
              <span className={rejectReason.length < 20 ? 'text-danger-500' : 'text-success-500'}>{rejectReason.length}/20 characters min</span>
            </div>
            {rejectError && <p className="text-xs text-danger-text">{rejectError}</p>}
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="secondary" onClick={() => setRejectingId(null)}>Cancel</Button>
              <Button onClick={handleReject} disabled={rejectReason.trim().length < 20 || rejectMutation.isPending} className="bg-danger-500 hover:bg-danger-600 text-white">Confirm Rejection</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
